/**
 * Gobodo Tab Configuration Service
 *
 * CRUD for TabConfiguration__c records.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { handleGobodoError, logGobodoError } from '../../utils/gobodoErrorHandler';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { normalizeToGobodoFormat } from '../../utils/objectApiNameNormalizer';
import { serializeGobodoData } from '../providers/gobodoRoleProvider';
import type { TabConfiguration, ConfiguredTab } from '../queries/tabConfigQueries';

const OBJECT_KEY = 'TabConfiguration__c';

function toIsoString(val: any): string {
  if (val == null) return new Date().toISOString();
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && typeof val.toISOString === 'function') return val.toISOString();
  return new Date().toISOString();
}

function gobodoToTabConfig(record: any): TabConfiguration {
  const raw = record.properties || record.data || {};
  const data = serializeGobodoData(raw) as Record<string, any>;
  let tabs: ConfiguredTab[] = [];
  try {
    const rawTabs = data.tabsJson;
    if (typeof rawTabs === 'string' && rawTabs) tabs = JSON.parse(rawTabs);
    else if (Array.isArray(rawTabs)) tabs = rawTabs;
  } catch {
    tabs = [];
  }
  return {
    id: String(record.id),
    objectName: data.objectTypeKey || '',
    application: data.application || undefined,
    tabs,
    createdBy: String(record.creatorid || data.createdBy || 'system'),
    createdDate: toIsoString(record.creationdate ?? data.createdAt),
    lastModified: toIsoString(record.modificationdate ?? data.updatedAt),
    lastModifiedBy: String(data.updatedBy || record.creatorid || 'system'),
  };
}

function configToGobodoProps(config: Partial<TabConfiguration>): Record<string, any> {
  return {
    objectTypeKey: config.objectName,
    application: config.application ?? '',
    tabsJson: config.tabs ? JSON.stringify(config.tabs) : '[]',
    isDefault: config.tabs?.some(t => t.isDefault) ?? false,
    createdBy: config.createdBy || 'system',
    updatedBy: config.lastModifiedBy || config.createdBy,
  };
}

/**
 * Normalize object name to API form (Client__c or Client__stg per REACT_APP_GOBODO_CUSTOM_SUFFIX)
 */
export function objectNameToApiName(name: string): string {
  const objToBase: Record<string, string> = {
    obj_lead: 'Lead', obj_client: 'Client', obj_contact: 'Contact',
    obj_opportunity: 'Opportunity', obj_case: 'Case', obj_event: 'Event',
    obj_task: 'Task', obj_report: 'Report', obj_csaudit: 'CSAudit', obj_pawrecord: 'PAWRecord',
  };
  if (objToBase[name]) return normalizeToGobodoFormat(objToBase[name]);
  if (name.endsWith('__c') || name.endsWith('__stg')) return name;
  if (name.startsWith('obj_')) {
    const base = name.replace(/^obj_/, '');
    const words = base.split('_');
    const pascal = words.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
    return normalizeToGobodoFormat(pascal);
  }
  return name;
}

export async function fetchTabConfigurations(
  objectTypeKey: string,
  application?: string
): Promise<TabConfiguration[]> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const allRecords = await retryWithBackoff(
    () =>
      objectsApi.getObjectsInGraphByType(GobodoConfig.graph(), OBJECT_KEY, { page: 0, size: 100 }),
    { maxRetries: 2 }
  );

  let records = allRecords || [];
  records = records.filter((r: any) => {
    const p = serializeGobodoData(r.properties || r.data || {}) as Record<string, any>;
    return (p.objectTypeKey || '') === objectTypeKey;
  });
  if (application && application !== 'all') {
    records = records.filter((r: any) => {
      const p = serializeGobodoData(r.properties || r.data || {}) as Record<string, any>;
      return p.application === application || p.application === 'Global' || !p.application;
    });
  }

  return records
    .map((r: any) => gobodoToTabConfig(r))
    .sort((a: TabConfiguration, b: TabConfiguration) => a.lastModified.localeCompare(b.lastModified));
}

export async function fetchTabConfiguration(id: string): Promise<TabConfiguration> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const record = await retryWithBackoff(() => objectsApi.getObjectById(parseInt(id)), {
    maxRetries: 3,
  });
  return gobodoToTabConfig(record);
}

export async function createTabConfiguration(
  data: Omit<TabConfiguration, 'id' | 'createdDate' | 'lastModified'> & {
    createdBy?: string;
    lastModifiedBy?: string;
  }
): Promise<TabConfiguration> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  await ensureObjectTypeExists(OBJECT_KEY, {
    label: 'Tab Configuration',
    pluralLabel: 'Tab Configurations',
    description: 'Tab bar configuration per object and application',
    icon: 'Layout',
    category: 'system',
  });

  const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
  const objectType = await retryWithBackoff(() => objectTypesApi.getObjectTypeByKey(OBJECT_KEY, { maxFields: 0 }), {
    maxRetries: 3,
  });

  const apiName = objectNameToApiName(data.objectName);
  const props = configToGobodoProps({
    ...data,
    objectName: apiName,
    createdBy: data.createdBy || 'system',
  });

  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = OBJECT_KEY;
  payload.typeid = objectType.id as any;
  payload.label = `${apiName} - ${data.application || 'All'}`;
  payload.graphs = [GobodoConfig.graph()];
  payload.properties = props;

  const created = await retryWithBackoff(() => objectsApi.createObject(payload), { maxRetries: 3 });
  return gobodoToTabConfig(created);
}

export async function updateTabConfiguration(
  id: string,
  updates: Partial<Pick<TabConfiguration, 'tabs' | 'application' | 'objectName'>>
): Promise<TabConfiguration> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const existing = await retryWithBackoff(() => objectsApi.getObjectById(parseInt(id)), {
    maxRetries: 3,
  });
  const current = gobodoToTabConfig(existing);

  const merged: TabConfiguration = {
    ...current,
    ...updates,
  };
  const props = configToGobodoProps(merged);
  if (updates.objectName) props.objectTypeKey = objectNameToApiName(updates.objectName);

  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = OBJECT_KEY;
  payload.typeid = (existing as any).typeid;
  payload.label = existing.label;
  payload.graphs = [GobodoConfig.graph()];
  payload.properties = { ...(existing.properties || existing.data || {}), ...props };

  const updated = await retryWithBackoff(() => objectsApi.updateObject(parseInt(id), payload), {
    maxRetries: 3,
  });
  return gobodoToTabConfig(updated);
}

export async function deleteTabConfiguration(id: string): Promise<void> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  try {
    await retryWithBackoff(() => objectsApi.deleteObject(parseInt(id)), { maxRetries: 3 });
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'deleteTabConfiguration', objectId: id });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw error;
  }
}
